/**
 * PriceHistory - Line chart showing trade prices over time.
 * Allows filtering by time range.
 */

import { memo, useMemo, useState, useCallback } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { cn } from '../../lib/utils'
import type { Trade } from '../../engine/types'

/** Available time range filters */
export type TimeRange = '1m' | '5m' | '15m' | 'all'

export interface PriceHistoryProps {
  /** Trades to display */
  trades: Trade[]
  /** Initial time range filter */
  timeRange?: TimeRange
  /** Chart height in pixels */
  height?: number
  /** Optional className for the container */
  className?: string
}

/** Data point for the chart */
interface PricePoint {
  timestamp: number
  price: number
  priceDisplay: number
  timeDisplay: string
}

/** Time range in milliseconds */
const TIME_RANGES: Record<TimeRange, number | null> = {
  '1m': 60 * 1000,
  '5m': 5 * 60 * 1000,
  '15m': 15 * 60 * 1000,
  all: null,
}

/** Time range labels */
const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  '1m': '1 min',
  '5m': '5 min',
  '15m': '15 min',
  all: 'All',
}

/**
 * Formats a timestamp for display.
 */
function formatTime(timestamp: number): string {
  const date = new Date(timestamp)
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/**
 * Custom tooltip component.
 */
function PriceTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ payload: PricePoint }>
}) {
  if (!active || !payload?.[0]) return null

  const data = payload[0].payload

  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 shadow-md">
      <p className="text-sm font-medium text-foreground">
        {data.priceDisplay}¢
      </p>
      <p className="text-xs text-muted-foreground">{data.timeDisplay}</p>
    </div>
  )
}

/**
 * TimeRangeButton - Filter button for time ranges.
 */
const TimeRangeButton = memo(function TimeRangeButton({
  range,
  isActive,
  onClick,
}: {
  range: TimeRange
  isActive: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-2 py-1 text-xs font-medium transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted text-muted-foreground hover:bg-muted/80'
      )}
      aria-pressed={isActive}
    >
      {TIME_RANGE_LABELS[range]}
    </button>
  )
})

/**
 * PriceHistory displays trade prices over time.
 */
export const PriceHistory = memo(function PriceHistory({
  trades,
  timeRange: initialTimeRange = 'all',
  height = 200,
  className,
}: PriceHistoryProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>(initialTimeRange)

  // Filter and transform trades
  const chartData = useMemo(() => {
    if (trades.length === 0) return []

    const now = Date.now()
    const rangeMs = TIME_RANGES[timeRange]

    // Filter by time range
    const filtered = rangeMs
      ? trades.filter((t) => now - t.timestamp <= rangeMs)
      : trades

    // Sort by timestamp ascending
    const sorted = [...filtered].sort((a, b) => a.timestamp - b.timestamp)

    // Transform to chart data
    return sorted.map((trade): PricePoint => ({
      timestamp: trade.timestamp,
      price: trade.price,
      priceDisplay: Math.round(trade.price * 100),
      timeDisplay: formatTime(trade.timestamp),
    }))
  }, [trades, timeRange])

  // Calculate statistics
  const stats = useMemo(() => {
    if (chartData.length === 0) {
      return { current: null, change: null, changePercent: null }
    }

    const current = chartData[chartData.length - 1].price
    const first = chartData[0].price
    const change = current - first
    const changePercent = first > 0 ? (change / first) * 100 : 0

    return {
      current: Math.round(current * 100),
      change: Math.round(change * 100),
      changePercent: changePercent.toFixed(1),
    }
  }, [chartData])

  const handleRangeChange = useCallback((range: TimeRange) => {
    setTimeRange(range)
  }, [])

  // Calculate Y-axis domain with some padding
  const yDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 100]

    const prices = chartData.map((d) => d.priceDisplay)
    const min = Math.min(...prices)
    const max = Math.max(...prices)
    const padding = Math.max((max - min) * 0.1, 5)

    return [
      Math.max(0, Math.floor(min - padding)),
      Math.min(100, Math.ceil(max + padding)),
    ]
  }, [chartData])

  return (
    <div
      className={cn('rounded-lg border border-border bg-card p-4', className)}
      role="img"
      aria-label="Price history chart"
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-foreground">Price History</h3>
          {stats.current !== null && (
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-bold tabular-nums text-foreground">
                {stats.current}¢
              </span>
              {stats.change !== null && stats.change !== 0 && (
                <span
                  className={cn(
                    'text-xs font-medium',
                    stats.change > 0 ? 'text-green-600' : 'text-red-600'
                  )}
                >
                  {stats.change > 0 ? '+' : ''}
                  {stats.change}¢ ({stats.changePercent}%)
                </span>
              )}
            </div>
          )}
        </div>

        {/* Time range filters */}
        <div className="flex gap-1">
          {(Object.keys(TIME_RANGES) as TimeRange[]).map((range) => (
            <TimeRangeButton
              key={range}
              range={range}
              isActive={timeRange === range}
              onClick={() => handleRangeChange(range)}
            />
          ))}
        </div>
      </div>

      {/* Chart */}
      {chartData.length === 0 ? (
        <div
          className="flex items-center justify-center text-sm text-muted-foreground"
          style={{ height }}
        >
          No trades yet. Execute a trade to see price history.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="timeDisplay"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={yDomain}
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value: number) => `${value}¢`}
              width={40}
            />
            <Tooltip content={<PriceTooltip />} />

            {/* 50% reference line */}
            <ReferenceLine
              y={50}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="3 3"
              strokeOpacity={0.5}
            />

            {/* Price line */}
            <Line
              type="monotone"
              dataKey="priceDisplay"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={chartData.length <= 20}
              activeDot={{ r: 4, fill: 'hsl(var(--primary))' }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}

      {/* Trade count */}
      {chartData.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {chartData.length} {chartData.length === 1 ? 'trade' : 'trades'} in
          this period
        </p>
      )}
    </div>
  )
})
